import { Icon, type IconName } from '../../components/icons/Icon'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import type { LifecycleStep, LifecycleStepId, LifecycleStepStatus } from './lifecycle'

/**
 * The Case Lifecycle Progress Tracker (film credit lifecycle polish, GA/NY/LA) — a horizontal
 * stepper over `deriveLifecycle`'s pure output (`lifecycle.ts`; no fetch happens here, this is a
 * presentational component only). Every stage's icon is a contextual, jurisdiction-agnostic stage
 * icon (config-not-code — the same six icons for every state/program); status (done/current/
 * upcoming) is conveyed by BOTH color and a real `StatusPill` icon+text label
 * (design-system.md §2.3/§7 "status conveyed by icon + text, never color alone"), reusing the exact
 * same `StatusPill` variants every other list/tab in this codebase uses (`success`/`info`/`muted`) —
 * never a literal hex, never the reserved chart-series palette repurposed for a status.
 *
 * Accessible: an ordered `<ol role="list">` (explicit `role="list"` restores list semantics VoiceOver
 * otherwise drops once `list-style: none` is applied), one `<li>` per stage, `aria-current="step"`
 * on the single current stage. Responsive: `flexWrap: 'wrap'` lets stages reflow onto more rows on a
 * narrow viewport instead of ever causing page-level horizontal scroll.
 */

const STEP_ICON: Record<LifecycleStepId, IconName> = {
  assessment: 'search',
  qualifiedSpend: 'bar-chart',
  certified: 'check-circle',
  monetized: 'dollar-sign',
  dossierFinalized: 'archive',
  filedOutcome: 'landmark',
}

const STATUS_VARIANT: Record<LifecycleStepStatus, StatusPillVariant> = {
  done: 'success',
  current: 'info',
  upcoming: 'muted',
}

const STATUS_LABEL: Record<LifecycleStepStatus, string> = {
  done: 'Done',
  current: 'In progress',
  upcoming: 'Upcoming',
}

const BADGE_BORDER: Record<LifecycleStepStatus, string> = {
  done: 'var(--iw-success)',
  current: 'var(--iw-info)',
  upcoming: 'var(--iw-border-strong)',
}

const BADGE_BG: Record<LifecycleStepStatus, string> = {
  done: 'var(--iw-success-bg)',
  current: 'var(--iw-info-bg)',
  upcoming: 'var(--iw-neutral-100)',
}

const BADGE_FG: Record<LifecycleStepStatus, string> = {
  done: 'var(--iw-success)',
  current: 'var(--iw-info)',
  upcoming: 'var(--iw-text-subtle)',
}

export interface LifecycleTrackerProps {
  steps: LifecycleStep[]
}

export function LifecycleTracker({ steps }: LifecycleTrackerProps) {
  return (
    <nav aria-label="Case lifecycle">
      <ol
        role="list"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'stretch',
          gap: 'var(--iw-space-3)',
        }}
      >
        {steps.map((step, index) => (
          <li
            key={step.id}
            aria-current={step.status === 'current' ? 'step' : undefined}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--iw-space-2)',
              flex: '1 1 170px',
              minWidth: 170,
              maxWidth: '100%',
            }}
          >
            {index > 0 ? (
              <span
                aria-hidden="true"
                style={{
                  alignSelf: 'center',
                  width: 'var(--iw-space-4)',
                  height: 2,
                  flexShrink: 0,
                  background: steps[index - 1]?.status === 'done' ? 'var(--iw-success)' : 'var(--iw-border-strong)',
                }}
              />
            ) : null}
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                border: `2px solid ${BADGE_BORDER[step.status]}`,
                background: BADGE_BG[step.status],
                color: BADGE_FG[step.status],
              }}
            >
              <Icon name={STEP_ICON[step.id]} size={16} />
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span
                style={{
                  fontWeight: 'var(--iw-weight-medium)' as never,
                  fontSize: 'var(--iw-size-sm)',
                  color: 'var(--iw-text)',
                }}
              >
                {step.label}
              </span>
              <span>
                <StatusPill variant={STATUS_VARIANT[step.status]} label={STATUS_LABEL[step.status]} />
              </span>
              {step.detail ? (
                <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>{step.detail}</span>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </nav>
  )
}
